import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from '../../utils/cn';

const toastIcons = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const toastStyles = {
  success: {
    bg: 'bg-success/10 dark:bg-success/20',
    border: 'border-success/20 dark:border-success/30',
    text: 'text-success',
    icon: 'text-success',
  },
  error: {
    bg: 'bg-error/10 dark:bg-error/20',
    border: 'border-error/20 dark:border-error/30',
    text: 'text-error',
    icon: 'text-error',
  },
  warning: {
    bg: 'bg-warning/10 dark:bg-warning/20',
    border: 'border-warning/20 dark:border-warning/30',
    text: 'text-warning',
    icon: 'text-warning',
  },
  info: {
    bg: 'bg-info/10 dark:bg-info/20',
    border: 'border-info/20 dark:border-info/30',
    text: 'text-info',
    icon: 'text-info',
  },
};

export const Toast = ({ toast, onClose }) => {
  const Icon = toastIcons[toast?.type] || Info;
  const styles = toastStyles[toast?.type] || toastStyles.info;

  useEffect(() => {
    if (!toast) return;
    if (toast.duration && toast.duration !== Infinity) {
      const timer = setTimeout(() => {
        onClose();
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, [toast, toast?.duration, onClose]);

  if (!toast) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className={cn(
        'flex items-start gap-3 rounded-lg border p-4 shadow-lg backdrop-blur-sm',
        'min-w-[300px] max-w-[400px]',
        styles.bg,
        styles.border
      )}
    >
      <Icon className={cn('mt-0.5 h-5 w-5 flex-shrink-0', styles.icon)} />
      <div className="min-w-0 flex-1">
        {toast.title && (
          <div className={cn('mb-1 text-sm font-semibold', styles.text)}>{toast.title}</div>
        )}
        <div className={cn('text-sm', toast.title ? 'text-text-secondary' : styles.text)}>
          {toast.message}
        </div>
      </div>
      <button
        onClick={onClose}
        className={cn(
          'transition-theme flex-shrink-0 rounded-md p-1',
          'hover:bg-surface-elevated',
          'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
        )}
        aria-label="Close toast"
      >
        <X className="h-4 w-4 text-text-tertiary" />
      </button>
    </motion.div>
  );
};

const ToastContainer = ({ toasts = [], onClose }) => {
  if (!toasts || toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed right-4 top-4 z-[100] flex flex-col gap-2">
      <AnimatePresence>
        {toasts
          .filter(toast => toast && toast.id)
          .map(toast => (
            <div key={toast.id} className="pointer-events-auto">
              <Toast toast={toast} onClose={() => onClose(toast.id)} />
            </div>
          ))}
      </AnimatePresence>
    </div>
  );
};

export { ToastContainer };
export default ToastContainer;
